import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../config';

// ~80 emojis populares en 5 categorias
const EMOJI_CATEGORIES = {
  Caras: ['😀','😃','😄','😁','😆','😅','🤣','😂','🙂','🙃','😉','😊','😇','🥰','😍','🤩','😘','😋','😛','😜','🤪','😝','🤭','🤔','😎','🤓','🧐','🤨','😏','😒','😴','🥱','😪','🤤','😵','🤯','🥵','🥶'],
  Gestos: ['👍','👎','👏','🙌','👐','🤝','🙏','👌','✌️','🤞','🤟','🤘','✋','🖐️','🖖','👋','🤚','💪','🤲','✊','👊','🫶','🫰'],
  Corazones: ['❤️','🧡','💛','💚','💙','💜','🖤','🤍','🤎','💔','❣️','💕','💞','💓','💗','💖','💘','💝','💟'],
  Objetos: ['🎉','🎊','🎁','🏆','💎','💰','💵','💸','📱','💻','🔑','🔒','🔓','🔋','⚡','🔥','✨','🌟','⭐'],
  Simbolos: ['✅','❌','⚠️','❓','❗','💯','🆗','🆕','🔝','♻️','✔️','➕','➖','💲','🔄','🚀','📍','🎯'],
};

const ChatComposer = ({
  onSendText,            // (text) => Promise
  onSendVoice,           // {audioBlob, duration} => Promise
  onSendAttachment,      // (file, caption) => Promise<msg>
  onSendSticker,         // (sticker_id) => Promise
  onOpenCualaccion,      // () => void
  disabled,
  showCualaccion = true, // false en grupos
}) => {
  const [text, setText] = useState('');
  const [showEmojis, setShowEmojis] = useState(false);
  const [emojiCat, setEmojiCat] = useState('Caras');
  const [showStickers, setShowStickers] = useState(false);
  const [officialPack, setOfficialPack] = useState([]);
  const [community, setCommunity] = useState([]);
  const [stickerTab, setStickerTab] = useState('oficial'); // oficial | comunidad | mios
  const [myStickers, setMyStickers] = useState([]);
  const [recording, setRecording] = useState(false);
  const [duration, setDuration] = useState(0);
  const [uploading, setUploading] = useState(false);

  // Attachment preview
  const [pendingFile, setPendingFile] = useState(null); // {file, url, caption}

  const fileInputRef = useRef(null);
  const stickerUploadRef = useRef(null);
  const emojiBtnRef = useRef(null);
  const stickerBtnRef = useRef(null);
  const inputRef = useRef(null);

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const startTimeRef = useRef(0);

  // Cerrar pickers al hacer click afuera
  useEffect(() => {
    const handler = (e) => {
      if (showEmojis && emojiBtnRef.current && !emojiBtnRef.current.parentElement.parentElement.contains(e.target)) {
        setShowEmojis(false);
      }
      if (showStickers && stickerBtnRef.current && !stickerBtnRef.current.parentElement.parentElement.contains(e.target)) {
        setShowStickers(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showEmojis, showStickers]);

  // Cargar stickers cuando se abre el picker
  useEffect(() => {
    if (showStickers && officialPack.length === 0) {
      axios.get(`${API}/stickers/official-pack`).then(r => setOfficialPack(r.data.pack || [])).catch(() => {});
    }
    if (showStickers) {
      axios.get(`${API}/stickers/community?limit=60`).then(r => setCommunity(r.data.stickers || [])).catch(() => {});
      axios.get(`${API}/stickers/my-stickers`).then(r => setMyStickers(r.data.stickers || [])).catch(() => {});
    }
  }, [showStickers, officialPack.length]);

  const insertEmoji = (e) => {
    setText(prev => prev + e);
    inputRef.current?.focus();
  };

  // ============ ATTACHMENTS ============
  const onFilePicked = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) {
      alert('El archivo supera 25 MB');
      return;
    }
    const url = f.type.startsWith('image/') ? URL.createObjectURL(f) : null;
    setPendingFile({ file: f, url, caption: '' });
    e.target.value = '';
  };

  const cancelPendingFile = () => {
    if (pendingFile?.url) URL.revokeObjectURL(pendingFile.url);
    setPendingFile(null);
  };

  const sendPendingFile = async () => {
    if (!pendingFile || !onSendAttachment) return;
    setUploading(true);
    try {
      await onSendAttachment(pendingFile.file, pendingFile.caption);
      cancelPendingFile();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Error subiendo archivo');
    } finally {
      setUploading(false);
    }
  };

  // ============ STICKERS ============
  const sendStickerHandler = async (sticker_id) => {
    if (!onSendSticker) return;
    setShowStickers(false);
    try {
      await onSendSticker(sticker_id);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Error enviando sticker');
    }
  };

  const onStickerUploadPicked = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) {
      alert('Sticker maximo 2 MB');
      return;
    }
    const label = window.prompt('Etiqueta del sticker (max 60 caracteres):', f.name.replace(/\.[^.]+$/, ''));
    if (!label) return;
    try {
      const fd = new FormData();
      fd.append('image', f);
      fd.append('label', label.slice(0, 60));
      await axios.post(`${API}/stickers/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const r = await axios.get(`${API}/stickers/my-stickers`);
      setMyStickers(r.data.stickers || []);
      setStickerTab('mios');
    } catch (err) {
      alert(err?.response?.data?.detail || 'Error subiendo sticker');
    }
  };

  // ============ VOICE ============
  const cleanupRec = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    recorderRef.current = null;
    chunksRef.current = [];
    setRecording(false);
    setDuration(0);
  };

  const startRec = async () => {
    if (recording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm';
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (ev) => { if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data); };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
      startTimeRef.current = Date.now();
      timerRef.current = setInterval(() => setDuration(Math.floor((Date.now() - startTimeRef.current) / 1000)), 500);
    } catch {
      alert('No se pudo acceder al microfono');
      cleanupRec();
    }
  };

  const cancelRec = () => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.stop(); } catch { /* ignore */ }
    }
    cleanupRec();
  };

  const stopAndSendRec = async () => {
    if (!recorderRef.current) { cleanupRec(); return; }
    const rec = recorderRef.current;
    const dur = Math.floor((Date.now() - startTimeRef.current) / 1000);
    if (dur < 1) { cancelRec(); return; }
    const blobReady = new Promise(res => { rec.onstop = () => res(new Blob(chunksRef.current, { type: rec.mimeType })); });
    try { rec.stop(); } catch { /* ignore */ }
    const blob = await blobReady;
    cleanupRec();
    setUploading(true);
    try {
      await onSendVoice({ audioBlob: blob, duration: dur });
    } catch (err) {
      alert(err?.response?.data?.detail || 'Error enviando nota de voz');
    } finally {
      setUploading(false);
    }
  };

  // ============ TEXT SEND ============
  const sendText = async (e) => {
    e?.preventDefault();
    const v = text.trim();
    if (!v || !onSendText) return;
    setText('');
    try { await onSendText(v); } catch (err) { setText(v); alert('Error enviando'); }
  };

  const fmtDur = (s) => `${String(Math.floor(s/60)).padStart(2,'0')}:${String(s%60).padStart(2,'0')}`;

  return (
    <div className="border-t border-gray-200 bg-white">
      {/* Pending file preview */}
      {pendingFile && (
        <div className="px-4 pt-3 pb-1" data-testid="attachment-preview">
          <div className="inline-flex items-center gap-3 bg-purple-50 border border-purple-200 rounded-xl p-2 max-w-md">
            {pendingFile.url ? (
              <img src={pendingFile.url} alt="preview" className="w-16 h-16 object-cover rounded-lg" />
            ) : (
              <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center">
                <svg className="w-7 h-7 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-800 truncate">{pendingFile.file.name}</p>
              <p className="text-xs text-gray-500">{(pendingFile.file.size / 1024).toFixed(1)} KB · {pendingFile.file.type}</p>
              <input
                type="text"
                value={pendingFile.caption}
                onChange={(e) => setPendingFile({ ...pendingFile, caption: e.target.value })}
                placeholder="Mensaje opcional..."
                data-testid="attachment-caption"
                className="mt-1 w-full px-2 py-1 border border-gray-200 rounded text-xs"
              />
            </div>
            <button onClick={cancelPendingFile} data-testid="cancel-attachment" className="w-6 h-6 rounded-full bg-red-500 text-white text-xs flex-shrink-0">×</button>
            <button onClick={sendPendingFile} disabled={uploading} data-testid="send-attachment-btn" className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white text-xs rounded font-medium disabled:opacity-50 flex-shrink-0">
              {uploading ? '...' : 'Enviar'}
            </button>
          </div>
        </div>
      )}

      {/* Recording bar */}
      {recording && (
        <div className="mx-4 mt-3 flex items-center gap-2 bg-red-50 border border-red-200 rounded-full px-4 py-2" data-testid="recording-bar">
          <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
          <span className="text-sm text-red-700 font-mono">{fmtDur(duration)}</span>
          <span className="text-xs text-red-600 flex-1">Grabando...</span>
          <button onClick={cancelRec} data-testid="cancel-record-btn" className="text-xs text-red-600 hover:text-red-800 font-medium">Cancelar</button>
          <button onClick={stopAndSendRec} data-testid="send-record-btn" className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded-full font-medium">Enviar</button>
        </div>
      )}

      <form onSubmit={sendText} className="p-4 flex items-center gap-2 relative">
        {/* CUALaccion */}
        {showCualaccion && !recording && !pendingFile && (
          <button
            type="button"
            onClick={onOpenCualaccion}
            data-testid="cualaccion-open-btn"
            title="CUALacción"
            className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white flex items-center justify-center shadow-sm flex-shrink-0"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </button>
        )}

        {/* Input + iconos internos */}
        {!recording && (
          <div className="flex-1 relative">
            <input
              ref={inputRef}
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={uploading ? 'Subiendo...' : 'Escribe un mensaje...'}
              disabled={disabled || uploading}
              data-testid="message-input"
              className="w-full pl-4 pr-32 py-2.5 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50"
            />

            {/* Iconos internos a la derecha del input */}
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
              {/* Stickers */}
              <button
                ref={stickerBtnRef}
                type="button"
                onClick={() => { setShowStickers(v => !v); setShowEmojis(false); }}
                data-testid="composer-stickers-btn"
                title="CUALsticker"
                className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${showStickers ? 'bg-purple-100 text-purple-700' : 'hover:bg-gray-100 text-gray-500 hover:text-purple-600'}`}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14.121 14.121L19 19m-7-7l7-7m-7 7l-2.879 2.879M12 12L9.121 9.121m0 5.758a3 3 0 10-4.243 4.243 3 3 0 004.243-4.243zm0-5.758a3 3 0 10-4.243-4.243 3 3 0 004.243 4.243z" />
                </svg>
              </button>

              {/* Emojis */}
              <button
                ref={emojiBtnRef}
                type="button"
                onClick={() => { setShowEmojis(v => !v); setShowStickers(false); }}
                data-testid="composer-emoji-btn"
                title="Emojis"
                className={`w-8 h-8 rounded-full flex items-center justify-center text-lg transition-colors ${showEmojis ? 'bg-amber-100' : 'hover:bg-gray-100 hover:bg-opacity-50'}`}
              >😊</button>

              {/* Adjuntar */}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                data-testid="composer-attach-btn"
                title="Adjuntar foto/archivo"
                className="w-8 h-8 rounded-full hover:bg-gray-100 text-gray-500 hover:text-purple-600 flex items-center justify-center transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
              </button>
            </div>

            {/* Emoji picker */}
            {showEmojis && (
              <div className="absolute bottom-full mb-2 right-0 bg-white border border-gray-200 rounded-2xl shadow-xl p-3 w-80 z-30" data-testid="emoji-picker">
                <div className="flex gap-1 mb-2 text-xs border-b pb-2 overflow-x-auto">
                  {Object.keys(EMOJI_CATEGORIES).map(c => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setEmojiCat(c)}
                      className={`px-2 py-1 rounded font-medium whitespace-nowrap ${emojiCat === c ? 'bg-purple-100 text-purple-700' : 'text-gray-500 hover:bg-gray-100'}`}
                    >{c}</button>
                  ))}
                </div>
                <div className="grid grid-cols-8 gap-1 text-2xl max-h-48 overflow-y-auto">
                  {EMOJI_CATEGORIES[emojiCat].map((e, i) => (
                    <button
                      key={`${e}-${i}`}
                      type="button"
                      onClick={() => insertEmoji(e)}
                      className="hover:bg-gray-100 rounded p-1"
                    >{e}</button>
                  ))}
                </div>
              </div>
            )}

            {/* Sticker picker */}
            {showStickers && (
              <div className="absolute bottom-full mb-2 right-0 bg-white border border-gray-200 rounded-2xl shadow-xl p-3 w-96 z-30" data-testid="sticker-picker">
                <div className="flex gap-1 mb-2 text-xs border-b pb-2">
                  {[
                    { id: 'oficial', label: 'CUALsticker oficial' },
                    { id: 'comunidad', label: 'Comunidad' },
                    { id: 'mios', label: 'Mios' },
                  ].map(t => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setStickerTab(t.id)}
                      data-testid={`sticker-tab-${t.id}`}
                      className={`px-2 py-1 rounded font-medium ${stickerTab === t.id ? 'bg-purple-100 text-purple-700' : 'text-gray-500 hover:bg-gray-100'}`}
                    >{t.label}</button>
                  ))}
                  <div className="flex-1"></div>
                  <button
                    type="button"
                    onClick={() => stickerUploadRef.current?.click()}
                    data-testid="sticker-upload-btn"
                    title="Crear sticker"
                    className="px-2 py-1 rounded bg-amber-100 text-amber-700 font-medium hover:bg-amber-200"
                  >+ Crear</button>
                  <input ref={stickerUploadRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={onStickerUploadPicked} />
                </div>

                <div className="grid grid-cols-4 gap-2 max-h-64 overflow-y-auto">
                  {stickerTab === 'oficial' && officialPack.map(s => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => sendStickerHandler(s.id)}
                      data-testid={`sticker-${s.id}`}
                      className="aspect-square rounded-xl flex flex-col items-center justify-center p-2 hover:scale-105 transition-transform shadow-sm"
                      style={{ background: `linear-gradient(135deg, ${s.color}22 0%, ${s.color}55 100%)`, border: `2px solid ${s.color}88` }}
                    >
                      <span className="text-3xl">{s.emoji}</span>
                      <span className="text-[10px] mt-1 font-medium text-gray-700 leading-tight text-center">{s.label}</span>
                    </button>
                  ))}
                  {stickerTab === 'comunidad' && community.map(s => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => sendStickerHandler(s.id)}
                      className="aspect-square rounded-xl bg-gray-50 hover:bg-gray-100 flex flex-col items-center justify-center p-2 border border-gray-200"
                      title={`Por ${s.author_name}`}
                    >
                      <img src={s.url} alt={s.label} className="w-12 h-12 object-contain" />
                      <span className="text-[10px] mt-1 text-gray-600 truncate w-full text-center">{s.label}</span>
                    </button>
                  ))}
                  {stickerTab === 'comunidad' && community.length === 0 && (
                    <p className="col-span-4 text-xs text-gray-400 text-center py-4">No hay stickers de la comunidad aun. Crea el primero.</p>
                  )}
                  {stickerTab === 'mios' && myStickers.map(s => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => sendStickerHandler(s.id)}
                      className="aspect-square rounded-xl bg-purple-50 hover:bg-purple-100 flex flex-col items-center justify-center p-2 border border-purple-200 relative"
                    >
                      <img src={s.url} alt={s.label} className="w-12 h-12 object-contain" />
                      <span className="text-[10px] mt-1 text-purple-700 truncate w-full text-center">{s.label}</span>
                      {s.claimed && <span className="absolute top-1 right-1 w-3 h-3 bg-amber-400 rounded-full" title="Reclamado"></span>}
                    </button>
                  ))}
                  {stickerTab === 'mios' && myStickers.length === 0 && (
                    <p className="col-span-4 text-xs text-gray-400 text-center py-4">Aun no has creado stickers. Pulsa "+ Crear" para subir el primero.</p>
                  )}
                </div>

                <p className="text-[10px] text-gray-400 mt-2 text-center">
                  Modelo fair use: los stickers de la comunidad circulan libremente. Cada autor puede reclamar autoria si lo desea.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Mic */}
        {!recording && !text.trim() && !pendingFile && (
          <button
            type="button"
            onClick={startRec}
            disabled={uploading}
            data-testid="record-voice-btn"
            title="Grabar nota de voz"
            className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 flex items-center justify-center flex-shrink-0 disabled:opacity-50"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
            </svg>
          </button>
        )}

        {/* Send */}
        {!recording && text.trim() && (
          <button
            type="submit"
            disabled={uploading}
            data-testid="send-message-btn"
            className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-full disabled:opacity-50 flex-shrink-0"
          >Enviar</button>
        )}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt"
          capture="environment"
          className="hidden"
          onChange={onFilePicked}
        />
      </form>
    </div>
  );
};

export default ChatComposer;
